/**
 * "Copy link" — canon R9's `share` verb, which the Hub did not have.
 *
 * R9 names five daily verbs — find, open, add, **share**, "what's the latest / what changed" — and
 * rules that "any verb that loses to the old way is a **defect**, because that verb is where truth
 * leaks back to laptops". On the X drive sharing is right-click → copy link → paste. In the Hub there
 * was no way to hand a colleague a document at all: the rows are real `<a href>` elements, so
 * "copy link address" technically worked, but it copied the DOWNLOAD route — which starts a download
 * in the recipient's browser with no record around it, and is not an affordance anyone would find.
 *
 * ## ⚠️ What this copies, and why it is not the file
 *
 * A link to the RECORD, anchored at the document (`documentShareUrl`). Never a store URL: canon R3
 * says the physical store is reachable by no human account — "not a mapped drive, **not a share**,
 * not a browsable path" — and R4 requires retrieval to be attributable. A share carrying a store URL
 * would be both failures at once, and the recipient's retrieval would land on the sharer's name or
 * on nobody's.
 *
 * So the recipient signs in as themselves and their click writes their own `document_retrievals`
 * row. The link is worth exactly the access its holder already has, which is the property that makes
 * it safe to paste into an email.
 *
 * ## ⚠️ The clipboard can refuse, and the fallback is not "nothing happened"
 *
 * `navigator.clipboard` throws when the page is not focused, on an insecure origin, or when the
 * permission is denied. Swallowing that leaves a button that silently does nothing — so a refusal
 * shows the URL instead, selectable, and the person copies it by hand.
 */

import { useState } from 'react';

export interface CopyLinkButtonProps {
  /** The URL to copy. Built by `documentShareUrl` / `projectShareUrl` — never assembled inline. */
  url: string;
  /** Accessible label, e.g. `Copy link to Geotech Report.pdf`. */
  label: string;
  /** Injected so the failure branch is testable — the web package has no jsdom. */
  writeText?: (text: string) => Promise<void>;
}

type State = 'idle' | 'copied' | 'failed';

export function CopyLinkButton({ url, label, writeText }: CopyLinkButtonProps) {
  const [state, setState] = useState<State>('idle');

  async function copy(): Promise<void> {
    const write =
      writeText ??
      ((text: string) => {
        // Reached for at CLICK time, not at render: `navigator.clipboard` is undefined on an
        // insecure origin, and touching it during render would throw before the button exists.
        const clip = (globalThis as { navigator?: { clipboard?: { writeText?: unknown } } })
          .navigator?.clipboard;
        return typeof clip?.writeText === 'function'
          ? (clip.writeText as (t: string) => Promise<void>)(text)
          : Promise.reject(new Error('clipboard unavailable'));
      });
    try {
      await write(url);
      setState('copied');
      setTimeout(() => setState('idle'), 1500);
    } catch {
      // Not silent: the URL is shown so the person can copy it themselves.
      setState('failed');
    }
  }

  return (
    <span className="copylink">
      <button
        type="button"
        className="copylink__btn"
        aria-label={label}
        // The target, on hover. A person about to paste a link into an email reasonably wants to see
        // where it goes first — and it is the only place the row states that the share points at the
        // RECORD rather than at the file, which is the R3/R4 property that makes it safe to send.
        title={url}
        onClick={() => void copy()}
      >
        {state === 'copied' ? 'Link copied' : 'Copy link'}
      </button>
      {state === 'failed' ? (
        <>
          {' '}
          <input
            className="copylink__fallback"
            type="text"
            readOnly
            value={url}
            aria-label={`${label} — copy manually`}
            onFocus={(e) => e.currentTarget.select()}
          />
        </>
      ) : null}
    </span>
  );
}
